import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {CoinsFountain} from "../coinfountain/CoinsFountain";
import {Timeline, TimelineLite, TweenLite} from "gsap";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {WinterHoardGameConfig} from "../WinterHoardGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {WinterHoardSoundConfig} from "../WinterHoardSoundConfig";
import {SoundPlayer} from "../sound/SoundPlayer";
import {ShakingDisplayObject} from "@nolimitcity/slot-game/bin/core/displayobject/ShakingDisplayObject";

export class BigWinView extends BaseView {

    private readonly _glowColor:number[] = [0x12d967,0xc7d022,0xef1c38];
    private _dimmer: PIXI.Sprite;
    private _glow: PIXI.Sprite;
    private _coinsFountain: CoinsFountain;
    private _currentTimeLine: TimelineLite | null;
    private _layer: ScalableLayer;
    private _scaleFactor: number;
    private _coinSoundIsPlaying: boolean = false;
    private _position: number[];

    private _shakeObject:ShakingDisplayObject;

    constructor(scale:number = 1, position:number[]= [360,360]) {
        super();
        this._scaleFactor = scale;
        this._position = position;
        this._dimmer = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._dimmer.anchor.set(0.5, 0.5);
        this._dimmer.position.set(360, 360);
        this._dimmer.tint = 0X000000;
        this._dimmer.alpha = 0;
        this._layer = StageManager.getLayer(WinterHoardGameConfig.instance.LAYERS.BIG_WIN.name);
        this._layer.addChild(this._dimmer);
        this._coinsFountain = new CoinsFountain(this._layer);

        this._shakeObject = new ShakingDisplayObject(StageManager.stage);
    }

    protected initAnimations(): void {
        this._glow = new PIXI.Sprite(GameResources.getTextures("bigwinGlow")[0]);
        this._glow.anchor.set(0.5, 0.8);
        this._glow.blendMode = PIXI.BLEND_MODES.ADD;
        this._glow.position.set(360, 360);
        this._glow.tint = this._glowColor[0];
        this._glow.alpha = 0;
        this._layer.addChild(this._glow);
    }

    protected onResize(resizeData: IResizeData): void {
        const screenWidth: number = resizeData.width;
        const screenHeight: number = resizeData.height;
        this._dimmer.width = screenWidth + 100;
        this._dimmer.height = screenHeight + 100;
        this._glow.scale.set((resizeData.orientation === Orientation.LANDSCAPE)?1.2:1);
        this._glow.position.y = resizeData.bottom;
    }

    public getBigWinLevelAnimation(level:number, type: string, duration: number): Timeline {
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._glow.tint = this._glowColor[level];
                this._coinsFountain.startFountain(level);
                this.playCoinSouns();
                if(level === 0){
                    SoundPlayer.pauseAmbience();
                    SoundPlayer.playEffect(WinterHoardSoundConfig.instance.BIG_WIN_MUSIC);
                    SoundPlayer.playEffect(WinterHoardSoundConfig.BIG_WIN_START);
                }
                this._shakeObject.shake(0.05, 2);
            },
            TweenLite.to(this._dimmer, 0.1, {alpha: 0.5}),
            TweenLite.to(this._glow, 0.1, {alpha: 1}),
            TweenLite.to(this, duration, {alpha: 1})
        ], 0);
        timeline.add(() => {
            this._shakeObject.stopShake();
        });
        return timeline;
    }

    private playCoinSouns():void{
        if(!this._coinSoundIsPlaying){
            this._coinSoundIsPlaying = true;
            SoundPlayer.playEffect(WinterHoardSoundConfig.COUNT_UP_COIN_LOOP, Infinity);
        }
    }

    public playEndingAnimation(): TimelineLite {
        const timeline: TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                SoundPlayer.resumeAmbience();
                SoundPlayer.stopEffect(WinterHoardSoundConfig.instance.BIG_WIN_MUSIC);
                this._coinsFountain.stopFountain();
                SoundPlayer.stopEffect(WinterHoardSoundConfig.COUNT_UP_COIN_LOOP);
                this._coinSoundIsPlaying = false;
            },
            TweenLite.to(this._dimmer, 1, {alpha: 0.5})
        ], 0);
        timeline.add([
            TweenLite.to(this._dimmer, 1, {alpha: 0}),
            TweenLite.to(this._glow, 1, {alpha: 0})
        ]);
        timeline.add(() => {
            this.reset();
        });

        return timeline;
    }

    public playAbortedEndingAnimation(gameMode: string): void {
        const timeline: TimelineLite = new TimelineLite();
         timeline.add([
            () => {
                SoundPlayer.resumeAmbience();
                SoundPlayer.stopEffect(WinterHoardSoundConfig.instance.BIG_WIN_MUSIC);
                this._coinsFountain.stopFountain();
                SoundPlayer.stopEffect(WinterHoardSoundConfig.COUNT_UP_COIN_LOOP);
                this._coinSoundIsPlaying = false;
                this._shakeObject.stopShake();
            },
            TweenLite.to(this._dimmer, 1, {alpha: 0}),
            TweenLite.to(this._glow, 1, {alpha: 0})
        ]);
        timeline.add(() => {
            this.reset();
        });

        timeline.play();
    }

    private reset(): void {
        if (this._currentTimeLine && this._currentTimeLine.isActive()) {
            this._currentTimeLine.pause();
            this._currentTimeLine.kill();
        }
        this._currentTimeLine = null;
    }

    public stopShake():void {
        this._shakeObject.stopShake();
    }
}